import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import './Services.css';
import { ShoppingBagIcon } from '../ShoppingBagIcon';
import { BlocksIcon } from '../BlocksIcon';
import { BoltIcon } from '../BoltIcon';
import { StarIcon } from '../StarIcon';
import { SettingsIcon } from '../SettingsIcon';
import { LinkIcon } from '../LinkIcon';
import { ZapIcon } from '../ZapIcon';
import { BookOpenIcon } from '../BookOpenIcon';

const Services = () => {
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('packaging');
  const shouldReduceMotion = useReducedMotion();

  // Products that should always show text first (flip behavior reversed)
  const flipExclusionList = [
    'End-to-End Packaging Solutions'
    // Add more product titles here that need reversed flip behavior
  ];

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCardInteraction = (cardId, hasImage) => {
    if (!hasImage) return;

    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  // Animation variants for better performance and consistency
  const cardVariants = {
    initial: { opacity: 0, y: 50 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        mass: 0.8
      }
    },
    hover: {
      y: isMobile ? 0 : -12,
      scale: isMobile ? 1 : 1.02,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    },
    tap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    }
  };

  const dissolveVariants = {
    front: {
      opacity: 1,
      scale: 1,
      transition: shouldReduceMotion ? { duration: 0.2 } : {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    back: {
      opacity: 0,
      scale: 0.95,
      transition: shouldReduceMotion ? { duration: 0.2 } : {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const imageVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: shouldReduceMotion ? { duration: 0.3, delay: 0.1 } : {
        duration: 0.3,
        delay: 0.1,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    initial: { scale: 1, rotate: 0 },
    hover: {
      scale: 1.15,
      rotate: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  const packagingSolutions = [
    {
      title: 'Heavy Duty Corrugated Boxes',
      description: 'Industrial-grade corrugated boxes designed for maximum strength and durability. Perfect for heavy industrial products requiring robust protection during storage and transportation.',
      icon: <ShoppingBagIcon size={32} />,
      image: '/assets/Heavy Duty Corrugated Boxes.png'
    },
    {
      title: 'Wooden Boxes & Crates',
      description: 'Premium quality wooden boxes and crates for heavy machinery and equipment. Custom-built to exact specifications for optimal protection and support.',
      icon: <BlocksIcon size={32} />,
      image: '/assets/Wooden Boxes & Crates.png'
    },
    {
      title: 'Wooden Fitments & Fixtures',
      description: 'Specialized wooden fitments and fixtures designed to secure your products. Custom engineering ensures perfect fit and maximum protection during transit.',
      icon: <BoltIcon size={32} />,
      image: '/assets/Wooden Fitments & Fixtures.jpg'
    },
    {
      title: 'Partition (Honey Comb) Corrugated Boxes',
      description: 'Advanced honeycomb partition boxes providing superior cushioning and separation. Ideal for multiple items requiring individual protection within a single package.',
      icon: <StarIcon size={32} />,
      image: '/assets/Partition Honey Comb Corrugated Boxes.png'
    },
    {
      title: 'Product Customization',
      description: 'Specialized in complete product customization. We design and manufacture packaging solutions tailored to your specific requirements and product dimensions.',
      icon: <SettingsIcon size={32} />,
      image: '/assets/Product Customization.png'
    },
    {
      title: 'End-to-End Packaging Solutions',
      description: 'Comprehensive packaging services from design to delivery. Complete packaging audit, testing, pilot trials, and implementation support for your industrial needs.',
      icon: <StarIcon size={32} />,
      image: '/assets/End-to-End Packaging Solutions.jpg'
    }
  ];

  const consumableProducts = [
    {
      title: 'Plastic Packing Strap & Clip',
      description: 'High-tensile plastic strapping and clips for secure bundling and reinforcement. Available in various widths and strengths for different load requirements.',
      icon: <LinkIcon size={32} />,
      image: '/assets/Plastic Packing Strap & Clip.jpg'
    },
    {
      title: 'Dunnage Air Bags',
      description: 'Inflatable dunnage air bags to fill void spaces and prevent cargo shifting during transport. Ensures load stability and reduces damage risk.',
      icon: <ZapIcon size={32} />,
      image: '/assets/Dunnage-Air-Bags.png'
    },
    {
      title: 'Cord Strap & Clip',
      description: 'Heavy-duty polyester cord strapping with buckles for maximum tensile strength. Ideal for securing heavy loads and extreme weather conditions.',
      icon: <LinkIcon size={32} />,
      image: '/assets/Cord Strap & Clip.png'
    },
    {
      title: 'Desiccant Pouch',
      description: 'Moisture-absorbing desiccant pouches protecting products from humidity damage during storage and shipping. Various sizes available for different applications.',
      icon: <ZapIcon size={32} />,
      image: '/assets/Desiccant Pouch.jpg'
    },
    {
      title: 'Plastic Shrink Wrap Sheet',
      description: 'Premium quality shrink wrap sheets for weather protection and load containment. Heat-activated for tight, secure wrapping of palletized goods.',
      icon: <BookOpenIcon size={32} />,
      image: '/assets/Plastic Shrink Wrap Sheet.webp'
    },
    {
      title: 'Bubble Sheet & More',
      description: 'Cushioning bubble sheets and various other packaging consumables. Complete range of protective materials for all your packaging needs.',
      icon: <StarIcon size={32} />,
      image: '/assets/Bubble Sheet.png'
    }
  ];

  const foodPackaging = [
    {
      title: 'Food-Grade Containers',
      description: 'FDA-approved food-safe containers and trays for direct food contact. Available in various materials including PP, PET, and biodegradable options.',
      icon: <ShoppingBagIcon size={32} />,
      image: '/assets/Food-Grade Containers.png'
    },
    {
      title: 'Vacuum Packaging Materials',
      description: 'High-barrier vacuum packaging films and pouches for extended shelf life. Ideal for fresh produce, meats, cheeses, and processed foods.',
      icon: <ZapIcon size={32} />,
      image: '/assets/Vacuum Packaging Materials.png'
    },
    {
      title: 'Modified Atmosphere Packaging',
      description: 'MAP solutions for preserving food freshness by controlling oxygen, nitrogen, and CO2 levels. Perfect for bakery, dairy, and ready-to-eat products.',
      icon: <SettingsIcon size={32} />,
      image: '/assets/Modified Atmosphere Packaging.png'
    },
    {
      title: 'Tamper-Evident Sealing',
      description: 'Security seals and tamper-proof packaging solutions ensuring product integrity from manufacturer to consumer. Builds customer trust and brand protection.',
      icon: <StarIcon size={32} />,
      image: '/assets/Tamper-Evident Sealing.png'
    },
    {
      title: 'Cold Chain Packaging',
      description: 'Insulated containers and temperature-controlled packaging for refrigerated and frozen foods. Maintains product quality during transit.',
      icon: <BlocksIcon size={32} />,
      image: '/assets/Cold Chain Packaging.png'
    },
    {
      title: 'Sustainable Food Packaging',
      description: 'Eco-friendly packaging solutions including biodegradable containers, compostable films, and recyclable materials meeting environmental regulations.',
      icon: <BookOpenIcon size={32} />,
      image: '/assets/Sustainable Food Packaging.png'
    }
  ];

  const pharmaPackaging = [
    {
      title: 'Blister Packs & Strip Packaging',
      description: 'Pharmaceutical-grade blister packs with aluminum or PVC backing for tablets and capsules. Child-resistant and tamper-evident options available.',
      icon: <SettingsIcon size={32} />,
      image: '/assets/Blister Packs & Strip Packaging.png'
    },
    {
      title: 'Medical-Grade Bottles & Vials',
      description: 'HDPE and glass bottles with child-resistant closures for liquid medications, syrups, and pharmaceutical formulations. USP Type I, II, and III glass available.',
      icon: <ShoppingBagIcon size={32} />,
      image: '/assets/Medical-Grade Bottles & Vials.jpg'
    },
    {
      title: 'Sterile Packaging Solutions',
      description: 'Cleanroom-manufactured sterile packaging for surgical instruments, medical devices, and injectable products. Meets ISO 11607 standards.',
      icon: <StarIcon size={32} />,
      image: '/assets/Sterile Packaging Solutions.png'
    },
    {
      title: 'Compliance & Serialization',
      description: 'Track-and-trace packaging with unique identifiers meeting FDA, EU FMD, and other regulatory requirements. Prevents counterfeiting and ensures supply chain integrity.',
      icon: <BookOpenIcon size={32} />,
      image: '/assets/Compliance & Serialization.png'
    },
    {
      title: 'Temperature-Sensitive Packaging',
      description: 'Validated thermal packaging systems for biologics, vaccines, and temperature-critical pharmaceuticals. Maintains 2-8°C or frozen conditions during distribution.',
      icon: <BoltIcon size={32} />,
      image: '/assets/Temperature-Sensitive Packaging.png'
    },
    {
      title: 'Unit Dose & Compliance Packaging',
      description: 'Organized medication packaging to improve patient adherence. Multi-dose blister cards, calendar packs, and pharmacy dispensing solutions.',
      icon: <LinkIcon size={32} />,
      image: '/assets/Unit Dose & Compliance Packaging.png'
    }
  ];

  // Tab configuration
  const tabs = [
    { id: 'packaging', label: 'Packaging Solutions', products: packagingSolutions },
    { id: 'consumable', label: 'Consumable Products', products: consumableProducts },
    { id: 'food', label: 'Food Packaging', products: foodPackaging },
    { id: 'pharma', label: 'Pharmaceutical Packaging', products: pharmaPackaging }
  ];

  const renderServiceCard = (service, index, category) => {
    const cardId = `${category}-${index}`;
    const isDissolved = flippedCards.has(cardId);
    const hasImage = !!service.image;
    const isExcluded = flipExclusionList.includes(service.title);
    
    // Reverse flip logic for excluded products
    const shouldShowImage = isExcluded ? isDissolved : !isDissolved;

    return (
      <motion.div
        key={index}
        className={`service-card fade-in ${hasImage ? 'has-slider' : ''}`}
        variants={cardVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
        whileTap="tap"
        onClick={() => isMobile && handleCardInteraction(cardId, hasImage)}
        onMouseEnter={() => hasImage && !isMobile && setFlippedCards(prev => new Set(prev).add(cardId))}
        onMouseLeave={() => hasImage && !isMobile && setFlippedCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(cardId);
          return newSet;
        })}
      >
        <div className="service-card-inner">
          {/* Front content - image or text based on exclusion list */}
          {hasImage ? (
            <>
              {/* Image side */}
              {shouldShowImage && (
                <motion.div
                  className="service-card-front"
                  animate={isDissolved ? "back" : "front"}
                  variants={dissolveVariants}
                >
                  <motion.img
                    src={service.image}
                    alt={service.title}
                    className="service-image"
                    initial={{ scale: 1, opacity: 1 }}
                    animate={{ scale: 1, opacity: 1 }}
                  />
                  <motion.div
                    className="service-image-overlay"
                    initial={{ opacity: 1, y: 0 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h4 className="service-image-title">{service.title}</h4>
                  </motion.div>
                </motion.div>
              )}
              
              {/* Text side */}
              <AnimatePresence>
                {!shouldShowImage && (
                  <motion.div
                    className="service-card-back"
                    variants={imageVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <motion.div
                      className="service-icon-wrapper"
                      variants={iconVariants}
                      whileHover="hover"
                    >
                      <div className="service-icon">{service.icon}</div>
                    </motion.div>
                    <div className="service-content">
                      <h3 className="service-title">{service.title}</h3>
                      <p className="service-description">{service.description}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          ) : (
            <motion.div
              className="service-card-front"
              animate="front"
              variants={dissolveVariants}
            >
              <motion.div
                className="service-icon-wrapper"
                variants={iconVariants}
                whileHover="hover"
              >
                <div className="service-icon">{service.icon}</div>
              </motion.div>
              <div className="service-content">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <section id="services" className="services">
      <div className="container">
        <motion.div
          className="services-header"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="section-title">Our Packaging Services</h2>
          <p className="section-description">
            Comprehensive packaging solutions across industrial, food, and pharmaceutical sectors
          </p>
        </motion.div>

        {/* Tabbed Packaging Categories */}
        <motion.div
          className="tabbed-categories"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="tabs-navigation">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              className="tab-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="services-grid"
                variants={{
                  animate: {
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.1
                    }
                  }
                }}
                initial="initial"
                animate="animate"
              >
                {tabs.find(tab => tab.id === activeTab)?.products.map((service, index) =>
                  renderServiceCard(service, index, activeTab)
                )}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
